"use client";

import { useReducer, useCallback, useRef, useEffect } from 'react';
import { clientLogger } from '@/lib/client-logger';

// ============================================================================
// Types
// ============================================================================

export type MintingStatus =
  | 'idle'
  | 'claiming'
  | 'minting'
  | 'polling'
  | 'success'
  | 'failed'
  | 'requires_wallet';

export interface MintingState {
  status: MintingStatus;
  claimId?: string;
  transactionId?: string;
  transactionUrl?: string;
  mintedAssetId?: string;
  errorMessage?: string;
  canRetry?: boolean;
  retryCount?: number;
}

// ============================================================================
// Action Types
// ============================================================================

type MintingAction =
  | { type: 'START_CLAIM' }
  | { type: 'CLAIM_CREATED'; claimId: string; hasMinting: boolean }
  | { type: 'REQUIRES_WALLET'; errorMessage: string }
  | { type: 'MINTING_SUCCESS'; claimId: string; transactionId?: string; transactionUrl?: string; mintedAssetId?: string }
  | { type: 'MINTING_FAILED'; claimId?: string; errorMessage: string; canRetry?: boolean; retryCount?: number }
  | { type: 'POLLING_TIMEOUT'; errorMessage: string }
  | { type: 'RETRY_START' }
  | { type: 'RESET' };

// ============================================================================
// Reducer
// ============================================================================

function mintingReducer(state: MintingState, action: MintingAction): MintingState {
  switch (action.type) {
    case 'START_CLAIM':
      return {
        status: 'claiming',
      };

    case 'CLAIM_CREATED':
      return {
        status: action.hasMinting ? 'minting' : 'success',
        claimId: action.claimId,
      };

    case 'REQUIRES_WALLET':
      return {
        status: 'requires_wallet',
        errorMessage: action.errorMessage,
      };

    case 'MINTING_SUCCESS':
      return {
        status: 'success',
        claimId: action.claimId,
        transactionId: action.transactionId,
        transactionUrl: action.transactionUrl,
        mintedAssetId: action.mintedAssetId,
      };

    case 'MINTING_FAILED':
      return {
        status: 'failed',
        claimId: action.claimId,
        errorMessage: action.errorMessage,
        canRetry: action.canRetry,
        retryCount: action.retryCount,
      };

    case 'POLLING_TIMEOUT':
      return {
        ...state,
        status: 'polling',
        errorMessage: action.errorMessage,
      };

    case 'RETRY_START':
      return {
        ...state,
        status: 'minting',
        errorMessage: undefined,
      };

    case 'RESET':
      return {
        status: 'idle',
      };

    default:
      return state;
  }
}

// ============================================================================
// Configuration
// ============================================================================

export interface MintingConfig {
  pollIntervalMs?: number;
  maxPollAttempts?: number;
}

const DEFAULT_CONFIG: Required<MintingConfig> = {
  pollIntervalMs: 2000,
  maxPollAttempts: 30,
};

// ============================================================================
// Hook
// ============================================================================

export interface UseMintingMachineOptions {
  clueId: string;
  campaignId: string;
  hasAtomicTemplateId: boolean;
  getOtp: () => string | null;
  getIykRef: () => string;
  getChipId: () => string;
  createDeviceHash: () => string;
  onOwnershipChange?: (owns: boolean, assetId?: string) => void;
  config?: MintingConfig;
}

export interface UseMintingMachineResult {
  state: MintingState;
  startClaim: () => Promise<void>;
  retry: () => Promise<void>;
  reset: () => void;
  isProcessing: boolean;
  canClaim: boolean;
}

export function useMintingMachine({
  clueId,
  campaignId,
  hasAtomicTemplateId,
  getOtp,
  getIykRef,
  getChipId,
  createDeviceHash,
  onOwnershipChange,
  config = {},
}: UseMintingMachineOptions): UseMintingMachineResult {
  const [state, dispatch] = useReducer(mintingReducer, { status: 'idle' });
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { pollIntervalMs, maxPollAttempts } = { ...DEFAULT_CONFIG, ...config };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Poll claim status
  const pollClaimStatus = useCallback(async (claimId: string): Promise<boolean> => {
    try {
      const response = await fetch(`/api/claims/${claimId}/status`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to check claim status');
      }

      const claim = data.claim;

      if (claim.status === 'CLAIMED') {
        // Success - stop polling
        dispatch({
          type: 'MINTING_SUCCESS',
          claimId,
          transactionId: claim.mintTransactionId,
          transactionUrl: data.transactionUrl,
          mintedAssetId: claim.mintedAssetId,
        });
        onOwnershipChange?.(true, claim.mintedAssetId);
        return true; // Done polling
      }

      if (claim.status === 'FAILED' || (claim.status === 'PENDING' && claim.errorMessage)) {
        // Failed - stop polling
        dispatch({
          type: 'MINTING_FAILED',
          claimId,
          errorMessage: claim.errorMessage || 'Minting failed. Please try again.',
          canRetry: data.canRetry,
          retryCount: claim.retryCount,
        });
        return true; // Done polling
      }

      // Still PENDING - continue polling
      return false;
    } catch (err) {
      clientLogger.error("Error polling claim status", {
        error: err instanceof Error ? err.message : "Unknown error",
        claimId
      });
      return false; // Continue polling on error
    }
  }, [onOwnershipChange]);

  // Start polling with interval
  const startPolling = useCallback((claimId: string) => {
    let pollCount = 0;

    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;
      const isDone = await pollClaimStatus(claimId);

      if (isDone || pollCount >= maxPollAttempts) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        if (pollCount >= maxPollAttempts && !isDone) {
          dispatch({
            type: 'POLLING_TIMEOUT',
            errorMessage: 'Minting is taking longer than expected. Please check back later.',
          });
        }
      }
    }, pollIntervalMs);

    // Initial poll
    pollClaimStatus(claimId);
  }, [pollClaimStatus, maxPollAttempts, pollIntervalMs]);

  // Start claim process
  const startClaim = useCallback(async () => {
    dispatch({ type: 'START_CLAIM' });

    try {
      const iykRef = getIykRef();
      const storedOtp = getOtp();
      const chipId = getChipId();

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (storedOtp) {
        headers['x-iyk-code'] = storedOtp;
      }

      const response = await fetch('/api/claim-asset', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          clueId,
          campaignId,
          chipId,
          refId: iykRef,
          deviceHash: createDeviceHash(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.requiresWallet) {
          dispatch({
            type: 'REQUIRES_WALLET',
            errorMessage: data.error || 'Please connect your WAX wallet to claim this digital asset',
          });
          return;
        }
        throw new Error(data.error || 'Failed to claim asset');
      }

      const claimId = data.claim?.id;
      if (!claimId) {
        throw new Error('No claim ID returned');
      }

      dispatch({
        type: 'CLAIM_CREATED',
        claimId,
        hasMinting: hasAtomicTemplateId,
      });

      if (hasAtomicTemplateId) {
        startPolling(claimId);
      } else {
        // Legacy claim - no minting
        onOwnershipChange?.(true);
      }
    } catch (err) {
      clientLogger.error("Error claiming asset", {
        error: err instanceof Error ? err.message : "Unknown error",
        clueId
      });
      dispatch({
        type: 'MINTING_FAILED',
        errorMessage: err instanceof Error ? err.message : 'An error occurred',
        canRetry: true,
      });
    }
  }, [clueId, campaignId, hasAtomicTemplateId, getOtp, getIykRef, getChipId, createDeviceHash, startPolling, onOwnershipChange]);

  // Retry minting
  const retry = useCallback(async () => {
    if (!state.claimId) {
      // No existing claim - start fresh
      await startClaim();
      return;
    }

    dispatch({ type: 'RETRY_START' });

    try {
      const response = await fetch(`/api/claims/${state.claimId}/retry`, {
        method: 'POST',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to retry minting');
      }

      startPolling(state.claimId);
    } catch (err) {
      clientLogger.error("Error retrying mint", {
        error: err instanceof Error ? err.message : "Unknown error",
        claimId: state.claimId
      });
      dispatch({
        type: 'MINTING_FAILED',
        claimId: state.claimId,
        errorMessage: err instanceof Error ? err.message : 'Retry failed',
        canRetry: true,
      });
    }
  }, [state.claimId, startClaim, startPolling]);

  // Reset state
  const reset = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    dispatch({ type: 'RESET' });
  }, []);

  const isProcessing = ['claiming', 'minting', 'polling'].includes(state.status);
  const canClaim = state.status === 'idle' || state.status === 'failed' || state.status === 'requires_wallet';

  return {
    state,
    startClaim,
    retry,
    reset,
    isProcessing,
    canClaim,
  };
}
